/**
 * Optional workspace panels, supplied by the build.
 *
 * Some builds of this application carry additional workspace panels that
 * others do not. Rather than have pages test for them, each page renders the
 * slot it owns and gets `null` when the running build supplied nothing —
 * which is the ordinary case and costs a single undefined check.
 *
 * A slot receives whatever the page already has in hand and returns a node.
 * It never fetches on the page's behalf and never decides authorization:
 * every panel here is a convenience over data the server already agreed to
 * send, and the server refuses independently of whichever build is running.
 */

import type { ReactNode } from 'react';

import type { DataRoomDocumentDetail } from './api.js';

export interface WorkspaceSlots {
  /** Extra links within the authenticated Owner navigation groups. */
  readonly ownerNavigation?: (context: {
    readonly placement: 'sidebar' | 'mobile';
  }) => ReactNode;
  /** Extra fields on the document-creation form. */
  readonly documentCreateFields?: (context: {
    readonly value: string | null;
    readonly onChange: (value: string) => void;
  }) => ReactNode;
  /**
   * A marker naming who a document is for, rendered wherever a document is
   * named: the detail header, every listing row, every search result, the
   * archive, and a task's pinned reference.
   *
   * It takes the audience rather than the document so that one slot covers
   * all of those shapes, and so that this bundle carries no opinion about
   * what the value means. `undefined` is the ordinary case on a build whose
   * server does not send the field; the slot decides to render nothing.
   */
  readonly documentBadge?: (context: {
    readonly audience: string | undefined;
  }) => ReactNode;
  /** Extra actions in a document's action cluster. */
  readonly documentActions?: (context: {
    readonly document: DataRoomDocumentDetail;
    readonly onChanged: () => void;
  }) => ReactNode;
  /** Extra controls beside the Data Room's download action. */
  readonly dataRoomActions?: () => ReactNode;
  /** Extra links on the Data Room root. */
  readonly dataRoomLinks?: () => ReactNode;
  /** Extra routes, mounted inside the workspace shell. */
  readonly routes?: readonly {
    readonly path: string;
    readonly element: ReactNode;
  }[];
}

let supplied: WorkspaceSlots = {};

/** Called once, by an entry point, before the application renders. */
export function registerWorkspaceSlots(slots: WorkspaceSlots): void {
  supplied = slots;
}

export function workspaceSlots(): WorkspaceSlots {
  return supplied;
}
